import React, { useState, useEffect, useRef } from "react";
import { CardBody, Row, Col, Spinner, Alert, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import DataTable, { TableColumn } from "react-data-table-component";
import Swal from "sweetalert2";
import "./index.css";
import ButtonComponent from "../../../../components/ButtonComponent";
import DeleteButtonComponent from "../../../../components/DeleteButtonComponent";
import {
  OrganizationMember,
  useAssignCustomerToOrganizationMutation,
  useGetUserSearchResultsMutation,
  useLazyGetMembersByOrganizationIdQuery,
  useRemoveCustomerFromOrganizationMutation, // Import the new mutation
} from "../../../../redux/services/orgService";
import TableLoadingComponent from "../../../../components/TableLoadingComponent";

interface AssignedCustomersProps {
  organization?: any;
  organizationId?: string;
  onCustomersChange?: (customers: Customer[]) => void;
}

interface Customer {
  documentId: string;
  displayName: string;
  email: string;
  userId: string;
  phoneNumber: string;
  isActive?: boolean;
  userType: string;
  addedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  memberId: string;
}

const AssignedCustomers: React.FC<AssignedCustomersProps> = ({
  organization,
  organizationId,
  onCustomersChange,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
  const [organizationMembers, setOrganizationMembers] = useState<
    OrganizationMember[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const [triggerUserSearchList] = useGetUserSearchResultsMutation();
  const [assignCustomerToOrganization, { isLoading: isAssigning }] =
    useAssignCustomerToOrganizationMutation();

  // New mutation for removing customer from organization
  const [removeCustomerFromOrganization, { isLoading: isRemoving }] =
    useRemoveCustomerFromOrganizationMutation();

  // Lazy query for fetching organization members
  const [
    triggerGetMembers,
    {
      data: membersData,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isLoading: isMembersLoading,
      error: membersErrorResponse,
    },
  ] = useLazyGetMembersByOrganizationIdQuery();

  // Fetch organization members when organizationId changes
  useEffect(() => {
    if (organizationId) {
      fetchOrganizationMembers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // Map organization members to selected customers when data is fetched
  useEffect(() => {
    if (membersData && organizationId) {
      const mappedCustomers: Customer[] = membersData.map(
        (member: OrganizationMember) => ({
          documentId: member.id,
          displayName: member.displayName,
          email: member.email,
          userId: member.userId,
          phoneNumber: "",
          userType: member.role,
          isActive: true,
          addedAt: member.addedAt,
          memberId: member.id,
        })
      );

      setSelectedCustomers(mappedCustomers);
      setOrganizationMembers(membersData);

      // Notify parent component
      if (onCustomersChange) {
        onCustomersChange(mappedCustomers);
      }
    }
  }, [membersData, organizationId, onCustomersChange]);

  // Handle errors from the members query
  useEffect(() => {
    if (membersErrorResponse) {
      const errorMessage = getErrorMessage(membersErrorResponse);
      setMembersError(errorMessage);
      toast.error(`Failed to load organization members: ${errorMessage}`);
    }
  }, [membersErrorResponse]);

  const fetchOrganizationMembers = async () => {
    if (!organizationId) return;

    setMembersLoading(true);
    setMembersError(null);

    try {
      await triggerGetMembers(organizationId).unwrap();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      setMembersError(errorMessage);
      toast.error(`Failed to load organization members: ${errorMessage}`);
    } finally {
      setMembersLoading(false);
    }
  };

  const getErrorMessage = (error: any): string => {
    if (typeof error === "string") return error;
    if (error?.data?.message) return error.data.message;
    if (error?.error) return error.error;
    return "Unknown error occurred";
  };

  // Function to format timestamp
  const formatTimestamp = (timestamp: {
    _seconds: number;
    _nanoseconds: number;
  }): string => {
    if (!timestamp || !timestamp._seconds) return "N/A";

    const date = new Date(
      timestamp._seconds * 1000 + timestamp._nanoseconds / 1000000
    );

    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleCustomerSearch = async (query: string) => {
    if (!query || query.length < 2) {
      setCustomers([]);
      setHasSearched(false);
      setShowDropdown(false);
      setSearchError(null);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setSearchError(null);
    setShowDropdown(true);

    try {
      const params = `query=${encodeURIComponent(
        query
      )}&filters=userType:Customer`;

      const response = await triggerUserSearchList({ params: params }).unwrap();

      // Map and filter customers
      const allCustomers =
        response?.hits?.map((item: any) => ({
          documentId: item.documentId,
          displayName: item.displayName,
          email: item.email,
          phoneNumber: item.phoneNumber,
          userType: item.userType,
          userId: item.userId,
          isActive: item.isActive !== false,
          memberId: "",
        })) || [];

      // Filter out customers without userId and those already in selected customers
      const validCustomers = allCustomers.filter((customer: Customer) => {
        const hasUserId = customer.userId && customer.userId.trim() !== "";
        const isAlreadyAdded = selectedCustomers.some(
          (sc) => sc.userId === customer.userId
        );

        return hasUserId && !isAlreadyAdded;
      });

      setCustomers(validCustomers);
    } catch (error: any) {
      setSearchError(
        "Failed to search customers. Please check Algolia configuration."
      );
      toast.error("Error searching for customers");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only search if there's no selected customer and search query has changed
    if (!selectedCustomer) {
      const timer = setTimeout(() => {
        handleCustomerSearch(searchQuery);
      }, 500);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedCustomer]);

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchQuery(customer.displayName);
    setShowDropdown(false);
    setCustomers([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // If user starts typing again after selecting a customer, clear the selection
    if (selectedCustomer && value !== selectedCustomer.displayName) {
      setSelectedCustomer(null);
    }
  };

  const handleInputFocus = () => {
    // Only show dropdown if we have search results and no customer is selected
    if (customers.length > 0 && !selectedCustomer) {
      setShowDropdown(true);
    }
  };

  const handleAddCustomer = async () => {
    if (!selectedCustomer) {
      toast.warning("Please select a customer first");
      return;
    }

    if (!organizationId) {
      toast.error("Organization ID is required");
      return;
    }

    if (!selectedCustomer.userId) {
      toast.error("Customer user ID is missing");
      return;
    }

    // Check if customer is already in the list
    if (selectedCustomers.find((c) => c.userId === selectedCustomer.userId)) {
      toast.warning("Customer is already assigned to this organization");
      return;
    }

    try {
      // Call the API to assign customer to organization
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const result = await assignCustomerToOrganization({
        customerUserId: selectedCustomer.userId,
        organizationId: organizationId,
      }).unwrap();


      // Refresh the members list after successful assignment
      await fetchOrganizationMembers();

      // Clear the selection
      setSelectedCustomer(null);
      setSearchQuery("");
      setCustomers([]);
      setShowDropdown(false);

      toast.success(
        `Customer ${selectedCustomer.displayName} assigned to organization successfully`
      );
    } catch (error: any) {

      // Show specific error message from API if available
      const errorMessage =
        error?.data?.message || "Failed to assign customer to organization";
      toast.error(errorMessage);
    }
  };

  const handleRemoveCustomer = async (customerId: string) => {
    const customerToRemove = selectedCustomers.find(
      (c) => c.documentId === customerId
    );

    if (!customerToRemove || !organizationId) {
      toast.error("Cannot remove customer: missing information");
      return;
    }

    // Show confirmation dialog using SweetAlert2
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to remove ${customerToRemove.displayName} from this organization?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    // If user confirms deletion
    if (result.isConfirmed) {
      try {
        // Call the API to remove customer from organization
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const response = await removeCustomerFromOrganization({
          memberId: customerToRemove.memberId,
          organizationId: organizationId,
        }).unwrap();

        
        // Remove from local state
        const updatedCustomers = selectedCustomers.filter(
          (c) => c.documentId !== customerId
        );
        setSelectedCustomers(updatedCustomers);

        // Also update organization members
        const updatedMembers = organizationMembers.filter(
          (m) => m.id !== customerToRemove.memberId
        );
        setOrganizationMembers(updatedMembers);

        // Notify parent component
        if (onCustomersChange) {
          onCustomersChange(updatedCustomers);
        }

        // Show success message
        Swal.fire(
          "Removed!",
          `${customerToRemove.displayName} has been removed from the organization.`,
          "success"
        );
      } catch (error: any) {

        // Show error message
        const errorMessage =
          error?.data?.message || "Failed to remove customer from organization";

        Swal.fire("Error!", errorMessage, "error");
      }
    }
  };

  // DataTable columns
  const columns: TableColumn<Customer>[] = [
    {
      name: <h6 className="fw-bold">Customer Name</h6>,
      selector: (row) => row.displayName,
      cell: (row) => (
        <div style={{ fontSize: "14px", fontWeight: "bold" }}>
          {row.displayName}
          {!row.isActive && (
            <Badge bg="warning" className="ms-2">
              Inactive
            </Badge>
          )}
        </div>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Email</h6>,
      selector: (row) => row.email,
      cell: (row) => <div style={{ fontSize: "14px" }}>{row.email}</div>,
      sortable: true,
    },
    // {
    //   name: <h6 className="fw-bold">Phone Number</h6>,
    //   selector: (row) => row.phoneNumber,
    //   cell: (row) => <div style={{ fontSize: "14px" }}>{row.phoneNumber || "N/A"}</div>,
    //   sortable: true,
    // },
    {
      name: <h6 className="fw-bold">User Type</h6>,
      selector: (row) => row.userType,
      cell: (row) => (
        <Badge bg="secondary" style={{ fontSize: "12px" }}>
          {row.userType}
        </Badge>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Created At</h6>,
      selector: (row) => row.addedAt._seconds,
      cell: (row) => (
        <div style={{ fontSize: "12px" }}>{formatTimestamp(row.addedAt)}</div>
      ),
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Action</h6>,
      cell: (row) => (
        <DeleteButtonComponent
          onClick={() => handleRemoveCustomer(row.documentId)}
          isDisabled={isAssigning || membersLoading || isRemoving}
        />
      ),
      sortable: false,
      width: "120px",
    },
  ];

  return (
    <CardBody>
      <div className="assigned-customers-container">
        {/* Search Section */}
        <div className="mb-4">
          <Row className="align-items-end">
            <Col lg="4">
              <div className="position-relative" ref={dropdownRef}>
                <div className="search-input-container">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Customers by Name,Email or Phone No"
                    value={searchQuery}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                  />
                </div>

                {/* Dropdown Results */}
                {showDropdown && (
                  <div className="dropdown-results card shadow-sm">
                    <div className="card-body p-0">
                      {loading ? (
                        <div className="text-center p-3">
                          <Spinner animation="border" size="sm" />
                          <span className="ms-2">Searching...</span>
                        </div>
                      ) : customers.length > 0 ? (
                        <div className="customers-dropdown-list">
                          {customers.map((customer) => (
                            <div
                              key={customer.documentId}
                              className={`dropdown-item p-3 border-bottom ${
                                selectedCustomer?.documentId ===
                                customer.documentId
                                  ? "bg-light"
                                  : ""
                              }`}
                              onClick={() => handleCustomerSelect(customer)}
                              style={{ cursor: "pointer" }}
                            >
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <strong>{customer.displayName}</strong>
                                  <div className="text-muted small">
                                    {customer.email}
                                  </div>
                                  <div className="text-muted small">
                                    {customer.phoneNumber}
                                  </div>
                                </div>
                                <div>
                                  <Badge bg="secondary" className="me-1">
                                    {customer.userType}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : hasSearched && !loading ? (
                        <div className="text-center p-3 text-muted">
                          No customers found matching "{searchQuery}"
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </Col>
            <Col lg="2">
              <ButtonComponent
                onClick={handleAddCustomer}
                isDisabled={!selectedCustomer || isAssigning || membersLoading}
                titel={isAssigning ? "Assigning..." : "Assign Customer"}
              />
            </Col>
          </Row>
        </div>

        {/* Error Alerts */}
        {searchError && (
          <Alert variant="danger" className="mb-3">
            <strong>Search Error:</strong> {searchError}
          </Alert>
        )}

        {membersError && (
          <Alert variant="danger" className="mb-3">
            <strong>Members Load Error:</strong> {membersError}
          </Alert>
        )}

        {/* Selected Customers Table */}
        {membersLoading ? (
          <div>
            <TableLoadingComponent numColumns={4} />
          </div>
        ) : selectedCustomers.length > 0 ? (
          <div className="selected-customers-section">
            <h6 className="mb-3">
              Assigned Customers ({selectedCustomers.length})
            </h6>
            <DataTable
              columns={columns}
              data={selectedCustomers}
              responsive
              pagination
              highlightOnHover
              pointerOnHover
              paginationPerPage={5}
              noDataComponent={<div>No customers assigned yet</div>}
              customStyles={{
                headCells: {
                  style: {
                    backgroundColor: "#f8f9fa",
                    fontWeight: "bold",
                  },
                },
              }}
            />
          </div>
        ) : (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-users fa-3x mb-3"></i>
            <p>No customers assigned yet. Search and assign customers above.</p>
          </div>
        )}
      </div>
    </CardBody>
  );
};

export default AssignedCustomers;
